/* eslint-disable jsx-a11y/anchor-is-valid */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import CircularSlider from 'react-circular-slider-svg';

import { JarvisWidget } from 'wdna-components/lib';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { ContextType, FilterContext } from '../../../../../types';
import { parameter, ParameterDO } from '../../../../configuration/requests';

const Summary: React.LazyExoticComponent<any> = React.lazy(() => import('./Summary'));

type CircleType = {
  hash?: boolean;
  name?: string;
  a?: string;
  data?: { x: number; y: string; z: string; }[];
}

type TempType = {
  c: number;
  w: number;
  h: number;
}

const DEFAULT_DATA: CircleType = {
  hash: true,
  name: '',
  a: '',
  data: [
    { x: 0.0, y: 'WEIGHT_CUR', z: 'kg' },
    { x: 0.0, y: 'AVERAGE', z: 'kg/ud' },
    { x: 0, y: 'UNLOADS', z: 'unloads' },
    { x: 0.0, y: 'WEIGHT_MAX', z: 'kg' },
    { x: 0, y: 'QUANTITY_TOTAL', z: 'containers' },
  ],
};

const CONTAINER_COLOR: Record<string, string> = {
  SUW: '#3A4749',
  FOR: '#654321',
  PAP: '#3895D3',
  PET: '#F7DC10',
  GLA: '#0B6623',
  PNG: '#654321',
  CRK: '#0B6623',
};

interface Props {
  title: string;
  className: string;
  container: string;
  code1: any;
  code2: any;
}

interface State {
  update: string;
  circleData: CircleType;
  mainFilter?: any;

  temp2W?: TempType;
  temp4W?: TempType;
}

export default class GraphCircle extends React.Component<Props, State> {
  private mounted: boolean;
  private userIsURW: boolean;

  private timer: NodeJS.Timeout | undefined;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;
    this.userIsURW = false;

    this.state = {
      update: window.getStr(),
      circleData: {},
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.userIsURW = hasLevel('role', ['ADM', 'URW']);

    const { update, dashboardData } = this.context?.parentState || {};
    this.setUpData(update, dashboardData);
    this.timer = setInterval(() => { this.dataTemperature(); }, 300000);
  }

  // TODO: change
  // eslint-disable-next-line camelcase
  public UNSAFE_componentWillReceiveProps(nextProps: Props, nextContext: ContextType): void {
    if (!_.isEqual(nextContext, this.context)) {
      const { update, dashboardData } = nextContext.parentState;
      this.setUpData(update, dashboardData);
    }
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentDidUpdate(): void {
    const newFilter: any = this.context?.parentState.mainFilter;
    const oldFilter: any = this.state.mainFilter;

    if (oldFilter !== newFilter && !_.isEmpty(newFilter)) {
      this.setLocalState({
        mainFilter: newFilter,
      }, () => {
        setTimeout((): void => { this.dataTemperature(); }, 3000);
      });
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    clearInterval(this.timer);
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public dataTemperature = (): void => {
    const subtype: string = this.state.circleData.name || '';
    const temperature2W: Record<string, number[]> = this.context?.parentState.temperature2W || {};
    const temperature4W: Record<string, number[]> = this.context?.parentState.temperature4W || [];

    const temp2W: TempType = { c: 0, w: 0, h: 0 };
    const temp4W: TempType = { c: 0, w: 0, h: 0 };

    if (subtype in temperature2W) {
      temp2W.c = temperature2W[subtype].filter((item: number) => item < 25).length;
      temp2W.w = temperature2W[subtype].filter((item: number) => item >= 25 && item < 49).length;
      temp2W.h = temperature2W[subtype].filter((item: number) => item >= 49).length;
    }

    if (subtype in temperature4W) {
      temp4W.c = temperature4W[subtype].filter((item: number) => item < 25).length;
      temp4W.w = temperature4W[subtype].filter((item: number) => item >= 25 && item < 49).length;
      temp4W.h = temperature4W[subtype].filter((item: number) => item >= 49).length;
    }

    this.setLocalState({
      temp2W,
      temp4W
    }, () => { this.forceUpdate(); });
  };

  public setUpData = (update: string, dashboardData: any = {}): void => {
    const { container } = this.props;
    const response: CircleType[] = dashboardData?.circleData || [];

    let data: CircleType[] = response.filter((group: CircleType) => group.name === container);
    if (data.length === 0) {
      const defaultData: CircleType = _.cloneDeep(DEFAULT_DATA);
      defaultData.name = container;
      data = [defaultData];
    }
    this.setLocalState({
      update,
      circleData: data[0],
    }, () => { this.forceUpdate(); });
  };

  public sliderColor = (): string => {
    const subtype: string = this.state.circleData.name || '';
    return CONTAINER_COLOR[subtype] || '';
  };

  public percentage = (value: number, maxim: number): number => {
    const percent: number = (value / Math.max(maxim, 1)) * 100;
    return Math.max(Math.min(percent, 100), 0);
  };

  public showSensorsType = async (): Promise<void> => {
    const { container, className } = this.props;
    const mainFilter: any = this.context?.parentState?.mainFilter;

    const [dashWaste] = await Promise.all([
      parameter<ParameterDO>('get', 'website_dashboard_waste')
    ]);

    const tabId: string = container;
    window.store.dispatch(removeTab(tabId));

    if ((dashWaste?.value === '1' && !this.userIsURW) || this.userIsURW) {
      const tabData: TabConfig = {
        id: tabId,
        active: true,
        title: `Sensors | Waste Containers ${container}`,
        component: Summary,
        props: {
          report: 'SENSORS_WASTE',
          title: 'Waste Containers',
          container,
          className,
          mainFilter
        },
      };

      window.store.dispatch(addTab(tabData));
    }
  };

  public render(): JSX.Element {
    const { title, container, className } = this.props;
    const { temp2W, temp4W, circleData } = this.state;
    const { data } = circleData || {};

    const curUnloadWeight: number = data?.[1].x || 0;
    const maxUnloadWeight: number = data?.[4].x || curUnloadWeight;
    const percent: number = this.percentage(curUnloadWeight, maxUnloadWeight);

    const degrees: number = (percent > 0) ? 323 - (percent * 286) / 100 : 323;
    const radians: number = degrees * (Math.PI / 180);
    const style: React.CSSProperties = { left: `${29 + 58 * Math.cos(radians)}px`, top: `${18 - 58 * Math.sin(radians)}px`, textAlign: 'center', fontSize: '12px' };

    const wheels2: number = data?.[6]?.x || 0;
    const wheels4: number = data?.[7]?.x || 0;

    return (
      <JarvisWidget
        editbutton={false}
        custombutton={false}
        togglebutton={false}
        fullscreenbutton={false}
        colorbutton={false}
        deletebutton={false}
        className="waste-main-dashboard"
      >
        <div className={`widget-body ${className} ${container}`}>
          <header className="container-header">
            <h2>
              <span onClick={(): void => { this.showSensorsType().catch(() => { /* pass */ }); }}>{title}</span>
              <small onClick={(): void => { this.showSensorsType().catch(() => { /* pass */ }); }}>{container}</small>
            </h2>
          </header>
          <div className="radial_graph">
            <div className="weight_values">
              <div className="container_percentage" style={style}>
                {percent.toFixed()}%
              </div>
              <div className="containers_total_weight">
                {data?.[1].x || 0}
              </div>
              <div className="containers_day_weight">
                {data?.[3].x || 0}<span>&nbsp;&nbsp;&nbsp;&nbsp;Kg/UNL</span>
              </div>
              <div className="container_unloads" style={{ paddingRight: '40px' }}>
                {data?.[2].x || 0}
              </div>
            </div>
            <div className="connection_signs">
              <div className="wave1" />
              <div className="wave2" />
            </div>
            <div className="circular_slider">
              <CircularSlider
                handle1={{
                  value: percent,
                  onChange: (): number => { return 100; },
                }}
                minValue={0}
                maxValue={100}
                size={160}
                startAngle={-53}
                endAngle={233}
                handleSize={15}
                arcColor={this.sliderColor()}
              />
            </div>
          </div>

          <div className="containers_stats big_containers">
            <div className="container_ico">
              <span>{(wheels4) || 0}</span>
            </div>
            {/* <div className="container_unloads"><span>18</span></div> */}

            <div className="containers_by_temp">
              <div className="hot_containers">{temp4W?.h || 0}</div>
              <div className="warm_containers">{temp4W?.w || 0}</div>
              <div className="cold_containers">{temp4W?.c || 0}</div>

              <div className="thermometer">
                <div className="celsius_symbol">&#176;C</div>
                <div className="thermometer_mask" />

                <div className="temps_blocks">
                  <div className="hot_temp_block" style={{ height: '40%' }} />
                  <div className="warm_temp_block" style={{ height: '25%' }} />
                  <div className="cold_temp_block" style={{ height: '35%' }} />
                </div>
                {/* Hay que poner estos valores dos veces para poder simular la forma del termómetro */}
                <div className="lower_temps_blocks">
                  <span className="mask">
                    <span className="bars_group">
                      <div className="hot_temp_block" style={{ height: '40%' }} />
                      <div className="warm_temp_block" style={{ height: '25%' }} />
                      <div className="cold_temp_block" style={{ height: '35%' }} />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="containers_stats small_containers">
            <div className="container_ico">
              <span>{(wheels2) || 0}</span>
            </div>
            {/* <div className="container_unloads"><span>18</span></div> */}

            <div className="containers_by_temp">
              <div className="hot_containers">{temp2W?.h || 0}</div>
              <div className="warm_containers">{temp2W?.w || 0}</div>
              <div className="cold_containers">{temp2W?.c || 0}</div>

              <div className="thermometer">
                <div className="celsius_symbol">&#176;C</div>
                <div className="thermometer_mask" />

                <div className="temps_blocks">
                  <div className="hot_temp_block" style={{ height: '40%' }} />
                  <div className="warm_temp_block" style={{ height: '25%' }} />
                  <div className="cold_temp_block" style={{ height: '35%' }} />
                </div>
                {/* Hay que poner estos valores dos veces para poder simular la forma del termómetro */}
                <div className="lower_temps_blocks">
                  <span className="mask">
                    <span className="bars_group">
                      <div className="hot_temp_block" style={{ height: '40%' }} />
                      <div className="warm_temp_block" style={{ height: '25%' }} />
                      <div className="cold_temp_block" style={{ height: '35%' }} />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </JarvisWidget>
    );
  }
}

export type GraphCircleType = typeof GraphCircle;
